import { Buttons } from "@/components/atoms/Buttons";
import { Button } from "@/components/ui/button";
import { HotkeyDecorator, type OS } from "@/components/atoms/HotkeyDecorator";
import { useContextHotkeys } from "@/hooks/useContextTree";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import React, { type ComponentProps, useMemo, useRef } from "react";
import { ArrowDownIcon, ArrowUpIcon, CornerDownLeftIcon } from "lucide-react";

type ButtonProps = ComponentProps<typeof Button>;

export type ButtonWithHotkey = ButtonProps & {
  hotkey?: string | Partial<Record<OS, string>>;
  /** Optional test selector forwarded to the rendered button. */
  "data-testid"?: string;
};

// Component to register a single button's hotkey
function ButtonHotkey({
  hotkey,
  onClick,
}: {
  hotkey?: string | Partial<Record<OS, string>>;
  onClick?: ButtonProps["onClick"];
}) {
  const handleHotkey = React.useCallback(
    (event: KeyboardEvent) => {
      // For plain "enter" hotkey, don't trigger if meta/cmd is pressed
      // This allows meta+enter to be handled by other handlers
      const hotkeyStr = typeof hotkey === "string" ? hotkey : "";
      const isPlainEnter = hotkeyStr.toLowerCase() === "enter";
      if (isPlainEnter && (event.metaKey || event.ctrlKey)) {
        return;
      }

      event.preventDefault();
      if (onClick) {
        // Create a synthetic event-like object for compatibility
        const syntheticEvent = {
          ...event,
          currentTarget: null,
          target: event.target,
          preventDefault: () => event.preventDefault(),
          stopPropagation: () => event.stopPropagation(),
        };
        onClick?.(
          syntheticEvent as unknown as React.MouseEvent<HTMLButtonElement>,
        );
      }
    },
    [onClick, hotkey],
  );

  // Always call hook (empty string means no registration)
  useContextHotkeys({
    combos: hotkey || "",
    callback: handleHotkey,
    id: "command-center-footer-button",
  });

  return null;
}

export function CommandCenterListFooter({
  buttons,
  // Legacy props for backward compatibility
  onSubmit,
  onSubmitLabel,
  SubmitIcon,
}: {
  buttons?: ButtonWithHotkey[];
  // Legacy props
  onSubmit?: () => void;
  onSubmitLabel?: string;
  SubmitIcon?: React.ElementType;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Build buttons array from legacy props or use provided buttons, and add hotkey badges
  const finalButtons: ButtonWithHotkey[] = useMemo(() => {
    let buttonArray: ButtonWithHotkey[];

    if (buttons && buttons.length > 0) {
      buttonArray = buttons;
    } else if (onSubmit) {
      // Legacy support: build button from onSubmit props
      buttonArray = [
        {
          variant: "default",
          size: "sm",
          children: (
            <>
              {onSubmitLabel || "Submit"}
              {SubmitIcon ? (
                <SubmitIcon size="1em" />
              ) : (
                <CornerDownLeftIcon size="1em" />
              )}
            </>
          ),
          onClick: onSubmit,
          hotkey: "enter",

          // Remove right padding for hotkey badge
          className: "pr-0",
        },
      ];
    } else {
      return [];
    }

    // A button with a hotkey ends in its badge - in the button's own text
    // colour, whatever the button and the scheme - its right padding less
    // for it.
    return buttonArray.map((button) =>
      button.hotkey
        ? {
            ...button,
            className: cn(button.className, "pr-1"),
            children: (
              <>
                {button.children}
                <HotkeyDecorator hotkey={button.hotkey} color="inherit" />
              </>
            ),
          }
        : button,
    );
  }, [buttons, onSubmit, onSubmitLabel, SubmitIcon]);

  // Extract hotkeys from buttons for registration (must be at top level)
  const buttonsWithoutHotkeys = useMemo(() => {
    return finalButtons.map(({ hotkey, ...button }) => button);
  }, [finalButtons]);

  return (
    <div
      ref={containerRef}
      className="flex justify-between items-center w-full"
    >
      <div className="flex flex-row gap-1 items-center pl-2 pr-2">
        <div className="bg-muted [border-width:1px] [border-style:solid] border-border rounded p-1 w-5.5 h-5.5 flex items-center justify-center">
          <ArrowUpIcon className="size-3 text-muted-foreground" />
        </div>
        <div className="bg-muted [border-width:1px] [border-style:solid] border-border rounded p-1 w-5.5 h-5.5 flex items-center justify-center">
          <ArrowDownIcon className="size-3 text-muted-foreground" />
        </div>

        <Typography
          level="body-md"
          className="text-muted-foreground/80 pb-1 pl-1"
        >
          Navigate
        </Typography>
      </div>
      {finalButtons.length > 0 && (
        <>
          {/* Register hotkeys for each button */}
          {finalButtons.map((button, index) => (
            <ButtonHotkey
              key={index}
              hotkey={button.hotkey}
              onClick={button.onClick}
            />
          ))}
          <Buttons buttons={buttonsWithoutHotkeys} />
        </>
      )}
    </div>
  );
}
